import React, { useContext, useEffect, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import Guideline from '../tools/Guideline';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import '../../../css/PdfActivity.scss';

import AuthService from '../../services/AuthService';
import lessonService from '../../services/LessonService';
import DirectionsFlyout from '../tabbedFlyout/DirectionsFlyout';
import useStyleEvents from '../../../hooks/useStyleEvents';

/**
 * PDF Activity: docReader-hosted PDF with page-based child questions in the flyout.
 * Lesson-driven PDF scroll is queued from LessonManager.setCurrentSlideIndex via DocReaderManager;
 * iframe load consumes sideNavPdfPage when the viewer wasn’t ready yet.
 */
const PdfActivity = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    toolbarManager,
    docReaderManager,
    studentActivityManager,
  } = useContext(MobXProviderContext);

  const model = lessonManager.getSlideModel(lessonElementId);
  const isPDF = model.entityTypeId === 'pdf_resource';
  const isDocReader = isPDF && model.pdfViewerTool === 'docreader';

  /**
   * DocReader URL must not be fetched during render: this component is a MobX observer and
   * re-renders on every page/sync update. Re-fetching returns a new presigned URL each time,
   * and assigning iframe.src forces a full PDF reload instead of goToPage().
   */
  useEffect(() => {
    if (!isDocReader || !model.contentItemId) {
      return undefined;
    }
    lessonService.fetchDocReaderUrl(model.contentItemId, null)
      .catch((error) => console.error('Error fetching doc reader URL:', error));
    return undefined;
  }, [isDocReader, model.contentItemId]);

  let iframeSrc = model.pageUrl;
  if (isPDF) {
    if (isDocReader) {
      iframeSrc = lessonManager.docReaderUrl || model.pageUrl || '';
    } else {
      iframeSrc = AuthService.getFlowpaperUrl(model);
    }
  } else if (iframeSrc && AuthService.isGoogleDoc(iframeSrc)) {
    iframeSrc = AuthService.fixGoogleDocUrl(iframeSrc);
  }

  useStyleEvents(lessonElementId, `${lessonElementId}-pdf-activity-view`);

  const backRef = useRef();
  const forwardRef = useRef();
  const highlightBtnRef = useRef();
  const annotationBtnRef = useRef();

  useAccessibilityClick(backRef, (event) => {
    event?.target?.blur?.();
    const element = document.getElementById(`${lessonElementId}-pdf-activity-iframe`);
    if (element && window.history.state !== null) {
      window.history.back();
    }
  });

  useAccessibilityClick(forwardRef, (event) => {
    event?.target?.blur?.();
    const element = document.getElementById(`${lessonElementId}-pdf-activity-iframe`);
    if (element) {
      window.history.forward();
    }
  });

  useAccessibilityClick(highlightBtnRef, (event) => {
    event?.target?.blur?.();
    docReaderManager.activateHighlightTool();
  });

  useAccessibilityClick(annotationBtnRef, (event) => {
    event?.target?.blur?.();
    docReaderManager.activateAnnotationTool();
  });

  useEffect(() => {
    window.history.pushState({ name: 'Stop' }, 'stop');
  }, []);

  useEffect(() => () => {
    docReaderManager.releasePdfActivityDocReaderSession();
  }, [lessonElementId]);

  useEffect(() => {
    const iframe = document.getElementById(`${lessonElementId}-pdf-activity-iframe`);
    if (iframe && model && model.pageUrl) {
      iframe.addEventListener('load', () => {
        if (studentActivityManager && studentActivityManager.studentActivityId) {
          const domain = `${model.pageUrl.split('//')[0]}//${model.pageUrl.split('//')[1].split('/')[0]}`;
          iframe.contentWindow.postMessage({ activity_id: studentActivityManager.studentActivityId }, domain);
        }
      });
    }
  }, [lessonElementId, model.pageUrl, studentActivityManager]);

  /**
   * First iframe load: wire docReader; apply `sideNavPdfPage` if setCurrentSlideIndex ran before iframe existed.
   */
  const initializeDocReaderFrame = async (id) => {
    docReaderManager.setDocumentSessionId(lessonElementId);
    docReaderManager.initializeDocReaderFrame(id);
    docReaderManager.setDocReaderFrameOwnerLessonElementId(lessonElementId);
    const pendingNav = docReaderManager.sideNavPdfPage;
    if (pendingNav != null && Number(pendingNav) >= 1) {
      docReaderManager.goToPage(pendingNav);
      docReaderManager.setSideNavPdfPage(null);
    } else if (model.startPage != null && model.startPage >= 1) {
      docReaderManager.goToPage(model.startPage);
    }
    const suppressPostLoadSync = docReaderManager.consumeSuppressSyncCurrentPageAfterPdfIframeLoad();
    if (!suppressPostLoadSync) {
      setTimeout(() => docReaderManager.syncCurrentPageFromReader(), 0);
    }
    lessonManager.alignDocReaderToCurrentPdfChildAfterIframeReady();
  };

  return (
    <div className='pdf-activity-question' id={`${lessonElementId}-pdf-activity-view`}>
      {(model.directions) ? <DirectionsFlyout directions={model.directions} isActivity={true} lessonElementId={model.lessonElementId} /> : ''}
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      {(!isPDF && model.showNavigationButtons) && (
        <div className='web-buttons'>
          <button ref={backRef} className='primary'>Back</button>
          <button ref={forwardRef} className='primary'>Forward</button>
        </div>
      )}
      <div className='test-item-question lesson-element pdf-activity' id={`${lessonElementId}-pdf-view`}>
        {isDocReader && docReaderManager.canEdit() && (
          <div className='pdf-activity-toolbar bookmark-toolbar'>
            <button ref={highlightBtnRef} className='bookmark-toolbar-btn' type='button'>Highlights</button>
            <button ref={annotationBtnRef} className='bookmark-toolbar-btn' type='button'>Annotations</button>
          </div>
        )}
        <iframe
          key={lessonElementId}
          allowFullScreen={true}
          className='PDFActivity'
          id={`${lessonElementId}-pdf-activity-iframe`}
          onLoad={() => {
            if (isDocReader) {
              initializeDocReaderFrame(`${lessonElementId}-pdf-activity-iframe`);
            }
          }}
          src={iframeSrc}
          title={model.title}
        />
      </div>
    </div>
  );
});

export default PdfActivity;
